import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Clock, ArrowRight, ClipboardText, Plus } from "@phosphor-icons/react";

const STATUS_TABS = ["all", "pending", "in_progress", "completed", "verified", "rejected"];

export default function Worklist() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [available, setAvailable] = useState([]);
  const [pickerErr, setPickerErr] = useState("");

  const load = async () => {
    setLoading(true);
    const params = { mine: true };
    if (tab !== "all") params.status = tab;
    const { data } = await api.get("/tasks", { params });
    setTasks(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, [tab]);

  const openPicker = async () => {
    setPickerErr("");
    setPickerOpen(true);
    try {
      const { data } = await api.get("/checklists/available");
      setAvailable(data);
    } catch (e) {
      setPickerErr(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const addTask = async (checklist_id) => {
    try {
      const { data } = await api.post("/tasks", { checklist_id, source: "manual" });
      setPickerOpen(false);
      navigate(`/task/${data.id}`);
    } catch (e) {
      setPickerErr(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const counts = STATUS_TABS.reduce((acc, s) => {
    acc[s] = s === "all" ? tasks.length : tasks.filter((t) => t.status === s).length;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="worklist-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-small text-blue-700">My Worklist</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "Manrope" }}>
            Assigned cleaning tasks
          </h1>
          <p className="text-sm text-slate-600 mt-1">Pick a task to execute, or add a self-initiated checklist.</p>
        </div>
        <button data-testid="add-self-task-button" onClick={openPicker} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md inline-flex items-center gap-2 transition-colors">
          <Plus size={14} weight="bold" /> Add task
        </button>
      </header>

      <div className="flex flex-wrap gap-1.5 border-b border-slate-200">
        {STATUS_TABS.map((s) => (
          <button
            key={s}
            data-testid={`worklist-tab-${s}`}
            onClick={() => setTab(s)}
            className={`px-3 py-2 text-sm font-semibold border-b-2 transition-colors -mb-px ${
              tab === s ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            {s.replace("_", " ").replace(/^\w/, (c) => c.toUpperCase())}
            <span className="ml-2 text-[10px] uppercase tracking-widest text-slate-400">{counts[s] ?? 0}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : tasks.length === 0 ? (
        <div className="flat-card p-12 text-center">
          <ClipboardText size={32} className="mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-500">No tasks here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tasks.map((t) => (
            <Link to={`/task/${t.id}`} key={t.id} data-testid={`task-card-${t.id}`} className="flat-card p-5 hover:border-blue-300 hover:-translate-y-[1px] transition-all block">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div className="label-small">{t.area_criticality} priority</div>
                  <h3 className="text-base font-bold text-slate-900 mt-0.5 truncate" style={{ fontFamily: "Manrope" }}>{t.area_name}</h3>
                </div>
                <span className={`status-pill status-${t.status}`}>{t.status.replace("_", " ")}</span>
              </div>
              <div className="text-xs text-slate-600 mb-3 truncate">{t.checklist_name}</div>
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span className="flex items-center gap-1"><Clock size={13} /> Due {new Date(t.due_at).toLocaleString()}</span>
                <ArrowRight size={14} className="text-blue-600" />
              </div>
            </Link>
          ))}
        </div>
      )}

      {pickerOpen && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 p-4" onClick={() => setPickerOpen(false)}>
          <div className="bg-white border border-slate-200 rounded-md max-w-xl w-full max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="checklist-picker-modal">
            <div className="label-small text-blue-700">Self-add task</div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Manrope" }}>Pick a checklist</h3>
            <p className="text-sm text-slate-600 mb-4">Only checklists for areas within your access scope are shown.</p>

            {pickerErr && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-3">{pickerErr}</div>}

            {available.length === 0 ? (
              <div className="text-sm text-slate-500 py-6 text-center">No checklists available.</div>
            ) : (
              <ul className="space-y-2">
                {available.map((c) => (
                  <li key={c.id}>
                    <button
                      data-testid={`pick-checklist-${c.id}`}
                      onClick={() => addTask(c.id)}
                      className="w-full text-left border border-slate-200 hover:border-blue-300 hover:bg-blue-50/30 rounded-md px-3 py-2.5 transition-colors flex items-center justify-between"
                    >
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{c.name}</div>
                        <div className="text-xs text-slate-500">{c.area_name} · {c.frequency} · TAT {c.tat_minutes ?? "—"} min</div>
                      </div>
                      <ArrowRight size={14} className="text-blue-600" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="flex justify-end mt-5">
              <button onClick={() => setPickerOpen(false)} className="px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 rounded-md transition-colors">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
